import React, { useCallback, useRef } from "react";
import Popover from "react-bootstrap/Popover";

import { GoogleDriveFilePreview } from "./GoogleDriveFilePreview";
import { Overlay, Tooltip } from "react-bootstrap";
import { Placement } from "react-bootstrap/esm/types";

export interface GoogleDriveFilePreviewPopoverProps {
  id: string;
  url: string;
  children: React.ReactElement<React.HTMLAttributes<HTMLElement> & React.RefAttributes<HTMLElement>>;
  popoverPlacement?: Placement;
}

/**
 * Renders a trigger element that, when clicked, shows a popover with a `GoogleDriveFilePreview` inside.
 *
 * The provided child element acts as the trigger for the overlay. When the trigger is hovered, it shows a tooltip with
 * the text "Preview". Clicking the trigger toggles the visibility of the document preview.
 */
export const GoogleDriveFilePreviewPopover: React.FC<GoogleDriveFilePreviewPopoverProps> = ({
  id,
  url,
  children: child,
  popoverPlacement,
}) => {
  const [showTooltip, setShowTooltip] = React.useState(false);
  const [showPopover, setShowPopover] = React.useState(false);
  const [popoverHiding, setPopoverHiding] = React.useState(false);
  const targetRef = useRef(null);

  const childOnClick = child.props.onClick;
  const childOnMouseEnter = child.props.onMouseEnter;
  const childOnMouseLeave = child.props.onMouseLeave;

  // Toggle the preview visibility on click
  const onClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      setShowPopover((prev) => !prev);
      setPopoverHiding(false);
      childOnClick?.(e);
    },
    [childOnClick],
  );

  // Show the tooltip on hover
  const onMouseEnter = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      setShowTooltip(true);
      childOnMouseLeave?.(e);
    },
    [childOnMouseLeave],
  );
  const onMouseLeave = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      setShowTooltip(false);
      childOnMouseEnter?.(e);
    },
    [childOnMouseEnter],
  );

  // Hide the overlay when the backdrop is clicked
  const onHide = useCallback(() => {
    setPopoverHiding(showPopover);
    setShowPopover(false);
    setShowTooltip(false);
  }, [showPopover]);
  const onExited = useCallback(() => {
    setPopoverHiding(false);
  }, []);

  // Clone the child element with our overridden event handlers
  const childWithHandler = React.cloneElement(child, {
    ref: targetRef,
    onClick: onClick,
    onMouseEnter: onMouseEnter,
    onMouseLeave: onMouseLeave,
  });

  return (
    <>
      {childWithHandler}
      <Overlay
        target={targetRef.current}
        show={showPopover || showTooltip}
        rootClose
        onHide={onHide}
        onExited={onExited}
        placement={showPopover ? popoverPlacement : undefined}
      >
        {(props) =>
          showPopover || popoverHiding ?
            <Popover id={`document-preview-popover_${id}`} {...props}>
              <Popover.Body>
                <GoogleDriveFilePreview
                  url={url}
                  onLoad={() => {
                    props.popper.scheduleUpdate?.();
                  }}
                />
              </Popover.Body>
            </Popover>
          : <Tooltip id={`document-preview-tooltip_${id}`} {...props}>
              Preview
            </Tooltip>
        }
      </Overlay>
    </>
  );
};
